import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@rb-serve-app/core';
import { EventType, useAnalytics } from '../ui/Analytics';

interface TooltipTextMap {
  [key: string]: string;
}

interface FlippedToolTipProps<T extends string | null> {
  tileId: string;
  title: string;
  value: string;
  icon: string;
  lang: string;
  tooltipTextMap: TooltipTextMap;
  activeTile: T;
  setActiveTile: Dispatch<SetStateAction<T>>;
  showTooltipMobile: T;
  setShowTooltipMobile: Dispatch<SetStateAction<T>>;
}

const FlippedToolTip = <T extends string | null>({
  tileId,
  title,
  value,
  icon,
  lang,
  tooltipTextMap,
  activeTile,
  setActiveTile,
  showTooltipMobile,
  setShowTooltipMobile,
}: FlippedToolTipProps<T>) => {
  console.log('tileId:', tileId, 'title:', title, 'activeTile:', activeTile);

  const { trackEvent } = useAnalytics();
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { assets } = contentfulData;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768);
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  useEffect(() => {
    if (!activeTile && !showTooltipMobile) return;

    const handleClickOutside = (e: MouseEvent) => {
      const tooltipElements = document.querySelectorAll(
        '[data-tooltip-tile], [data-info-icon], [data-tooltip-mobile]'
      );
      let clickedInside = false;
      tooltipElements.forEach((el) => {
        if (el.contains(e.target as Node)) {
          clickedInside = true;
        }
      });
      if (!clickedInside) {
        setActiveTile(null as T);
        setShowTooltipMobile(null as T);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeTile, showTooltipMobile, setActiveTile, setShowTooltipMobile]);

  const getTooltipLines = (tileId: string) => {
    const content = tooltipTextMap[tileId] || '';
    return content.split(/<br\s*\/?>/i).filter(Boolean);
  };

  const trackPageAndClickEvent = (tileId: string) => {
    let pageName = '';
    switch (tileId) {
      case 'rewards':
      case 'bonus':
        pageName = 'rewards landing page';
        break;
      case 'month':
      case 'earnedReward':
      case 'adjustment':
        pageName = 'earned cash back rewards - card flip';
        trackEvent(
          EventType.PAGE_VIEW,
          [pageName, 'rewards'],
          window.location.href,
          undefined
        );
        break;
      default:
        break;
    }

    trackEvent(
      EventType.CLICK_EVENT,
      [pageName, 'rewards'],
      window.location.href,
      `${pageName} | ${tileId}`
    );
  };

  const handleTileClick = (tileId: string) => {
    if (!isMobile) {
      setActiveTile((prev) => (prev === tileId ? null : tileId) as T);
    }
    trackPageAndClickEvent(tileId);
  };

  const handleMobileTooltipToggle = (e: React.MouseEvent, tileId: string) => {
    e.stopPropagation();
    setShowTooltipMobile((prev) => (prev === tileId ? null : tileId) as T);
  };

  return (
    <div
      data-tooltip-tile
      onClick={() => handleTileClick(tileId)}
      className={`relative w-full md:h-40 lg:h-48 cursor-pointer transition-transform duration-300
                ${!isMobile ? 'hover:scale-105' : ''} ${isMobile ? 'h-20' : ''}`}
    >
      <div className="w-full h-full [perspective:1000px]">
        <div
          className={`relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d]
                        ${!isMobile && activeTile === tileId ? '[transform:rotateY(180deg)]' : ''}`}
        >
          <div
            className="absolute w-full h-full bg-white border border-[#E2E2E2] rounded-[10px]
             flex px-4 [backface-visibility:hidden]"
          >
            <div className="m-auto flex items-center justify-center w-full">
              <img src={icon} alt={`${tileId}-icon`} className="w-10 h-10" />
              <div className="flex flex-col justify-center pl-4">
                <div
                  className="text-[#1F1F1F] text-base font-bold leading-[20px]"
                  aria-label={title}
                >
                  {title}
                </div>
                <div
                  aria-label={value}
                  className="text-[#1F1F1F] text-base font-medium leading-[24px]"
                >
                  {value}
                </div>
              </div>

              {isMobile && (
                <div className="ml-auto">
                  <img
                    src={assets?.infoToolTip}
                    alt="infoIcon"
                    className="w-6 h-6 cursor-pointer"
                    data-info-icon
                    onClick={(e) => handleMobileTooltipToggle(e, tileId)}
                  />
                </div>
              )}
            </div>
          </div>

          {!isMobile && (
            <div
              className="absolute w-full h-full bg-[#1E1E1E] text-[#fff] text-base font-medium p-3 rounded-[10px] overflow-auto
                            [transform:rotateY(180deg)] [backface-visibility:hidden]"
            >
              <div className="space-y-1">
                {getTooltipLines(tileId).map((line, idx) => (
                  <div
                    key={idx}
                    dangerouslySetInnerHTML={{ __html: line.trim() }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isMobile && showTooltipMobile === tileId && (
        <div
          data-tooltip-mobile
          className="absolute w-80 text-xs p-3 rounded-md shadow-lg bg-white text-[#1E1E1E] top-[calc(100% + 10px)] right-1 z-10"
        >
          <div className="space-y-1">
            {getTooltipLines(tileId).map((line, idx) => (
              <div
                key={idx}
                dangerouslySetInnerHTML={{ __html: line.trim() }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlippedToolTip;
